import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../config/api';
import './PaidNoCertAttempt.css';

const DEBOUNCE_MS = 350;

const formatPolicyDateOnly = (dateString) => {
  if (!dateString) return '—';
  const raw = String(dateString).trim();
  const parsed = raw.includes('/')
    ? (() => {
        const [day, month, year] = raw.split('/');
        return new Date(Number(year), Number(month) - 1, Number(day));
      })()
    : new Date(/^\d{4}-\d{2}-\d{2}/.test(raw) ? `${raw.slice(0, 10)}T12:00:00` : raw);
  if (isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Admin view: paid payments where certificate is not issued (and no or failed attempts).
 * Surfaces "paid but never attempted" for recovery (e.g. Recover paid cover).
 * Search by client name, vehicle registration (plate), or agent name.
 * Retry with date: for overlap / double-insurance cases; suggested start = day after latest other issued cover on same plate (our DB), merged with DMVIC when available.
 */
const PaidNoCertAttempt = ({ user, onShowNotification }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [recovering, setRecovering] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [retryDateModal, setRetryDateModal] = useState(null);
  const [suggestedStartDate, setSuggestedStartDate] = useState(null);
  const [suggestedStartDateFromDb, setSuggestedStartDateFromDb] = useState(null);
  const [suggestedStartDateFromDmvic, setSuggestedStartDateFromDmvic] = useState(null);
  const [latestOtherPolicyEndDate, setLatestOtherPolicyEndDate] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [cannotAutoReissue, setCannotAutoReissue] = useState(false);
  const [suggestedLookupMessage, setSuggestedLookupMessage] = useState(null);
  const [retryWithDateResult, setRetryWithDateResult] = useState(null);
  const [ntsaPlateModal, setNtsaPlateModal] = useState(null);
  const [ntsaPlateInput, setNtsaPlateInput] = useState('');
  const [ntsaPlateSaving, setNtsaPlateSaving] = useState(false);
  const [ntsaPlateStepResult, setNtsaPlateStepResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchApplied(searchInput.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async () => {
    const keepRows = list.length > 0;
    if (!keepRows) setLoading(true);
    setLoadError('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (searchApplied) params.set('search', searchApplied);
      const res = await fetch(`${getApiBaseUrl()}/api/admin/paid-no-cert-attempt?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) setList(data.data);
      else {
        setLoadError(data.error || 'Failed to load list');
        onShowNotification(data.error || 'Failed to load list', 'error');
      }
    } catch (e) {
      const msg = e.name === 'AbortError'
        ? 'The list took too long to load. Try searching by plate or refresh.'
        : (e.message || 'Error loading paid-no-cert list');
      setLoadError(msg);
      onShowNotification(msg, 'error');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [searchApplied]);

  const handleRecover = async (row) => {
    if (recovering) return;
    setRecovering(row.referenceNumber);
    try {
      const token = localStorage.getItem('adminToken');
      const body = {
        referenceNumber: row.referenceNumber,
        idNumber: row.idNumber || undefined
      };
      const res = await fetch(`${getApiBaseUrl()}/api/admin/recover-paid-cover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        onShowNotification(data.message || 'Recovery completed', 'success');
        load();
      } else {
        onShowNotification(data.error || 'Recovery failed', 'error');
      }
    } catch (e) {
      onShowNotification(e.message || 'Recovery request failed', 'error');
    } finally {
      setRecovering(null);
    }
  };

  const handleRetryIssuance = async (row) => {
    if (!row.clientId || retrying) return;
    setRetrying(row.clientId);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${getApiBaseUrl()}/api/admin/retry-certificate-issuance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: row.clientId })
      });
      const data = await res.json();
      if (data.success) {
        const msg = data.certificateNumber
          ? `Certificate issued: ${data.certificateNumber}.${data.certificateUrl ? ' Use the download link in the table.' : ''}`
          : (data.message || 'Retry queued or certificate issued.');
        onShowNotification(msg, 'success');
        load();
      } else {
        let msg = data.validationErrors?.length ? data.validationErrors.join(' ') : (data.error || 'Retry failed');
        if (data.hasDoubleInsurance && (data.suggestedStartDate || data.suggestedStartDateFromDb)) {
          msg += ` Suggested start: ${data.suggestedStartDate || data.suggestedStartDateFromDb}. Use "With date" to apply it.`;
        }
        onShowNotification(msg, 'error');
      }
    } catch (e) {
      onShowNotification(e.message || 'Retry request failed', 'error');
    } finally {
      setRetrying(null);
    }
  };

  const openRetryDateModal = async (row) => {
    if (!row?.clientId) return;
    setRetryDateModal(row);
    setSuggestedStartDate(null);
    setSuggestedStartDateFromDb(null);
    setSuggestedStartDateFromDmvic(null);
    setLatestOtherPolicyEndDate(null);
    setRetryWithDateResult(null);
    setCustomStartDate('');
    setCannotAutoReissue(false);
    setSuggestedLookupMessage(null);
    setLoadingSuggested(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(
        `${getApiBaseUrl()}/api/admin/suggested-certificate-start-date?clientId=${encodeURIComponent(row.clientId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setSuggestedStartDateFromDb(data.suggestedStartDateFromDb || null);
        setSuggestedStartDateFromDmvic(data.suggestedStartDateFromDmvic || null);
        setLatestOtherPolicyEndDate(data.latestOtherPolicyEndDate || null);
        setCannotAutoReissue(!!data.cannotAutoReissue);
        setSuggestedLookupMessage(data.message || null);
        if (data.suggestedStartDate) {
          setSuggestedStartDate(data.suggestedStartDate);
          setCustomStartDate(data.suggestedStartDate);
        } else if (data.cannotAutoReissue) {
          onShowNotification(
            'This vehicle already has active cover; next start is more than 30 days away. Removed from cert recovery.',
            'info'
          );
          load();
        }
      }
    } catch (e) {
      console.warn('Suggested start date fetch failed:', e.message);
      setSuggestedLookupMessage(e.message || 'Could not load suggested start date');
    } finally {
      setLoadingSuggested(false);
    }
  };

  const openNtsaPlateModal = (row) => {
    if (!row?.clientId) return;
    setNtsaPlateModal(row);
    setNtsaPlateInput((row.vehicleRegistration || '').trim());
    setNtsaPlateStepResult(null);
  };

  const closeNtsaPlateModal = () => {
    setNtsaPlateModal(null);
    setNtsaPlateInput('');
    setNtsaPlateStepResult(null);
    setNtsaPlateSaving(false);
  };

  const submitNtsaPlateCorrection = async () => {
    if (!ntsaPlateModal?.clientId || ntsaPlateSaving) return;
    const plate = String(ntsaPlateInput || '').trim();
    if (!plate) {
      onShowNotification('Enter the correct full registration.', 'error');
      return;
    }
    setNtsaPlateSaving(true);
    setNtsaPlateStepResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res1 = await fetch(`${getApiBaseUrl()}/api/admin/correct-plate-ntsa-mismatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: ntsaPlateModal.clientId, correctRegistrationNumber: plate })
      });
      const data1 = await res1.json().catch(() => ({}));
      if (!data1.success) {
        setNtsaPlateStepResult({ step: 'plate', success: false, error: data1.error || 'Update failed' });
        onShowNotification(data1.error || 'Could not update plate from NTSA', 'error');
        return;
      }
      const res2 = await fetch(`${getApiBaseUrl()}/api/admin/retry-certificate-issuance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: ntsaPlateModal.clientId })
      });
      const data2 = await res2.json().catch(() => ({}));
      setNtsaPlateStepResult({ step: 'retry', ...data2 });
      if (data2.success) {
        onShowNotification(
          data2.certificateNumber ? `Certificate issued: ${data2.certificateNumber}.` : (data2.message || 'Retry completed.'),
          'success'
        );
        load();
        if (data2.certificateNumber) {
          setTimeout(closeNtsaPlateModal, 1500);
        }
      } else {
        const msg = data2.validationErrors?.length ? data2.validationErrors.join(' ') : (data2.error || 'Retry failed');
        onShowNotification(msg, 'error');
      }
    } catch (e) {
      setNtsaPlateStepResult({ step: 'plate', success: false, error: e.message });
      onShowNotification(e.message || 'Request failed', 'error');
    } finally {
      setNtsaPlateSaving(false);
    }
  };

  const closeRetryDateModal = () => {
    setRetryDateModal(null);
    setSuggestedStartDate(null);
    setSuggestedStartDateFromDb(null);
    setSuggestedStartDateFromDmvic(null);
    setLatestOtherPolicyEndDate(null);
    setCustomStartDate('');
    setCannotAutoReissue(false);
    setSuggestedLookupMessage(null);
    setRetryWithDateResult(null);
  };

  const handleRetryWithDate = async () => {
    const reissueDate = String(suggestedStartDate || customStartDate || '').trim().slice(0, 10);
    if (!retryDateModal?.clientId || !reissueDate) {
      onShowNotification('Enter or confirm a start date first.', 'error');
      return;
    }
    setRetryWithDateResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${getApiBaseUrl()}/api/admin/retry-certificate-issuance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: retryDateModal.clientId, startDate: reissueDate })
      });
      const data = await res.json();
      setRetryWithDateResult(data);
      if (data.success) {
        onShowNotification(data.certificateNumber ? 'Certificate issued successfully.' : (data.message || 'Retry completed.'), 'success');
        load();
        if (data.certificateNumber) {
          setTimeout(closeRetryDateModal, 1500);
        }
      }
    } catch (e) {
      setRetryWithDateResult({ success: false, error: e.message });
      onShowNotification(e.message || 'Retry request failed', 'error');
    }
  };

  return (
    <div className="paid-no-cert-attempt">
      <header className="paid-no-cert-header">
        <div className="paid-no-cert-header-top">
          <h1 className="paid-no-cert-title">Paid – certificate not issued</h1>
          {!loading && list.length > 0 && (
            <span className="paid-no-cert-count">{list.length} item{list.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        <p className="paid-no-cert-desc">
          Payments completed but without a certificate (or with failed issuance). Search by plate number, client name, or agent. Use <strong>Recover</strong> to create client/vehicle if missing; <strong>Retry</strong> when client exists; <strong>With date</strong> when another cover on the same plate ends within 30 days (suggested start = day after that cover ends). Vehicles that already have active DMVIC cover with a next start more than 30 days away are omitted — they cannot be auto-reissued yet. When the last error is only the NTSA “details do not match” message, use <strong>Correct plate</strong> to enter the full registration from NTSA, refresh vehicle data, then retry issuance.
        </p>
      </header>

      <div className="paid-no-cert-toolbar">
        <div className="paid-no-cert-search-wrap">
          <span className="paid-no-cert-search-icon" aria-hidden>⌕</span>
          <input
            id="paid-no-cert-search"
            type="text"
            placeholder="Search by plate, client name or agent..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="paid-no-cert-search-input"
            aria-label="Search by client name, vehicle registration or agent name"
          />
          {searchApplied && (
            <button
              type="button"
              className="paid-no-cert-clear-search"
              onClick={() => { setSearchInput(''); setSearchApplied(''); }}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <button
          type="button"
          className="paid-no-cert-refresh"
          onClick={() => load()}
          disabled={loading}
          aria-label="Refresh list"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loadError && list.length === 0 && !loading ? (
        <div className="paid-no-cert-empty-card">
          <div className="paid-no-cert-empty-icon" aria-hidden>⚠️</div>
          <h3>Could not load list</h3>
          <p>{loadError}</p>
        </div>
      ) : loading && list.length === 0 ? (
        <div className="paid-no-cert-loading-card">
          <div className="paid-no-cert-spinner" aria-hidden />
          <p>Loading paid payments without certificate…</p>
        </div>
      ) : list.length === 0 ? (
        <div className="paid-no-cert-empty-card">
          <div className="paid-no-cert-empty-icon" aria-hidden>📋</div>
          <h3>{searchApplied ? 'No matches' : 'No pending items'}</h3>
          <p>{searchApplied ? 'Try a different search (plate, client or agent name).' : 'No paid payments without certificate found.'}</p>
        </div>
      ) : (
        <div className="paid-no-cert-card">
          <div className="paid-no-cert-table-wrap">
            <table className="paid-no-cert-table">
              <thead>
                <tr>
                  <th>Ref #</th>
                  <th>Client ID</th>
                  <th>Client name</th>
                  <th>Completed</th>
                  <th>Amount</th>
                  <th>Vehicle</th>
                  <th>Attempts</th>
                  <th>Last error</th>
                  <th className="paid-no-cert-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.paymentId || row.referenceNumber}>
                    <td><code className="paid-no-cert-ref">{row.referenceNumber}</code></td>
                    <td><span className="paid-no-cert-client-id">{row.clientId}</span></td>
                    <td>{row.insuredName || '–'}</td>
                    <td>{row.completedAt ? new Date(row.completedAt).toLocaleString() : '–'}</td>
                    <td className="paid-no-cert-amount">{row.amount != null ? Number(row.amount).toLocaleString() : '–'}</td>
                    <td><strong>{row.vehicleRegistration || '–'}</strong></td>
                    <td>
                      <span className={`paid-no-cert-badge paid-no-cert-badge-attempts ${row.noAttemptYet ? 'no-attempt' : ''}`}>
                        {row.attemptCount} {row.noAttemptYet ? '(none)' : ''}
                      </span>
                    </td>
                    <td className="last-error-cell" title={row.lastError || ''}>
                      {row.lastError ? String(row.lastError).slice(0, 80) + (row.lastError.length > 80 ? '…' : '') : '–'}
                    </td>
                    <td className="paid-no-cert-actions-cell">
                      <div className="paid-no-cert-actions">
                        <button
                          type="button"
                          className="btn-recover"
                          disabled={recovering === row.referenceNumber}
                          onClick={() => handleRecover(row)}
                        >
                          {recovering === row.referenceNumber ? '…' : 'Recover'}
                        </button>
                        {row.clientId && (
                          <>
                            <button
                              type="button"
                              className="btn-retry"
                              disabled={retrying === row.clientId}
                              onClick={() => handleRetryIssuance(row)}
                            >
                              {retrying === row.clientId ? '…' : 'Retry'}
                            </button>
                            <button
                              type="button"
                              className="btn-retry-date"
                              onClick={() => openRetryDateModal(row)}
                              title="Retry with a custom start date (e.g. after double insurance)"
                            >
                              With date
                            </button>
                            {row.ntsaRegistrationMismatchOnly && (
                              <button
                                type="button"
                                className="btn-correct-plate"
                                disabled={ntsaPlateSaving}
                                onClick={() => openNtsaPlateModal(row)}
                                title="Wrong or incomplete plate: enter full NTSA registration, sync vehicle, then retry certificate"
                              >
                                Correct plate
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {retryDateModal && (
        <div className="paid-no-cert-modal-overlay" onClick={closeRetryDateModal} role="dialog" aria-modal="true" aria-labelledby="retry-date-modal-title">
          <div className="paid-no-cert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="paid-no-cert-modal-header">
              <h3 id="retry-date-modal-title">Re-issue after active cover ends</h3>
              <button type="button" className="paid-no-cert-modal-close" onClick={closeRetryDateModal} aria-label="Close">×</button>
            </div>
            <p className="paid-no-cert-modal-meta">
              Client: {retryDateModal.insuredName || retryDateModal.clientId} · Vehicle: {retryDateModal.vehicleRegistration || '–'}
            </p>
            {loadingSuggested ? (
              <p className="paid-no-cert-modal-loading">Checking DMVIC for the next available start date…</p>
            ) : suggestedStartDate ? (
              <div className="paid-no-cert-modal-suggested">
                {latestOtherPolicyEndDate && (
                  <p style={{ marginTop: 0 }}>
                    Existing active cover ends on <strong>{formatPolicyDateOnly(latestOtherPolicyEndDate)}</strong>.
                  </p>
                )}
                <p>
                  Next available start date: <strong>{formatPolicyDateOnly(suggestedStartDate)}</strong>
                </p>
                <p className="paid-no-cert-modal-suggested-note" style={{ marginTop: '0.35rem' }}>
                  Day after the previous policy expires. This date will be saved and sent to DMVIC.
                </p>
              </div>
            ) : cannotAutoReissue ? (
              <div className="paid-no-cert-modal-suggested" style={{ color: '#721c24' }}>
                <p style={{ marginTop: 0, fontWeight: 600 }}>Cannot auto-reissue yet.</p>
                <p className="paid-no-cert-modal-suggested-note">
                  This vehicle already has active cover on DMVIC. The next available start date is more than 30 days away. It has been removed from cert recovery — try again when the current policy is within 30 days of expiry, or contact D-MVIC support.
                </p>
              </div>
            ) : (
              <div className="paid-no-cert-modal-suggested">
                <p style={{ marginTop: 0, fontWeight: 600 }}>No automatic start date found</p>
                <p className="paid-no-cert-modal-suggested-note">
                  {suggestedLookupMessage
                    ? `DMVIC lookup: ${suggestedLookupMessage}`
                    : 'DMVIC did not return a start date (or the check failed). Enter the day after the previous certificate ends (e.g. from the DMVIC dashboard), then re-issue.'}
                </p>
                <div className="paid-no-cert-modal-field" style={{ marginTop: '0.75rem' }}>
                  <label htmlFor="manual-reissue-start">Start date (YYYY-MM-DD)</label>
                  <input
                    id="manual-reissue-start"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </div>
              </div>
            )}
            {retryWithDateResult && (
              <div className={`paid-no-cert-modal-result ${retryWithDateResult.success ? 'success' : 'error'}`}>
                {retryWithDateResult.success ? (
                  <>
                    <p>{retryWithDateResult.certificateNumber ? 'Certificate issued successfully.' : (retryWithDateResult.message || 'Issuance queued.')}</p>
                    {retryWithDateResult.certificateNumber && (
                      <p>Certificate number: <strong>{retryWithDateResult.certificateNumber}</strong></p>
                    )}
                    {retryWithDateResult.certificateUrl && (
                      <a href={retryWithDateResult.certificateUrl} target="_blank" rel="noopener noreferrer" className="btn-download-cert">
                        Download certificate
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <p>{retryWithDateResult.error || 'Issuance failed.'}</p>
                    {retryWithDateResult.suggestedStartDate && (
                      <p className="suggested-hint">Suggested start date: {retryWithDateResult.suggestedStartDate}</p>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="paid-no-cert-modal-actions">
              <button
                type="button"
                className="btn-retry-date-submit"
                onClick={handleRetryWithDate}
                disabled={
                  loadingSuggested ||
                  cannotAutoReissue ||
                  !(suggestedStartDate || customStartDate)
                }
              >
                {(suggestedStartDate || customStartDate)
                  ? `Re-issue certificate from ${formatPolicyDateOnly(suggestedStartDate || customStartDate)}`
                  : 'Re-issue certificate'}
              </button>
              <button type="button" className="btn-retry-date-cancel" onClick={closeRetryDateModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {ntsaPlateModal && (
        <div className="paid-no-cert-modal-overlay" onClick={closeNtsaPlateModal} role="dialog" aria-modal="true" aria-labelledby="ntsa-plate-modal-title">
          <div className="paid-no-cert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="paid-no-cert-modal-header">
              <h3 id="ntsa-plate-modal-title">Correct plate from NTSA</h3>
              <button type="button" className="paid-no-cert-modal-close" onClick={closeNtsaPlateModal} aria-label="Close">×</button>
            </div>
            <p className="paid-no-cert-modal-meta">
              Client: {ntsaPlateModal.insuredName || ntsaPlateModal.clientId} · Current plate: {ntsaPlateModal.vehicleRegistration || '–'}
            </p>
            <p className="paid-no-cert-modal-suggested-note" style={{ marginBottom: '0.75rem' }}>
              Enter the full registration as on NTSA. Vehicle details will be refreshed from a VehicleSearch, then certificate issuance will run (same as Retry).
            </p>
            <div className="paid-no-cert-modal-field">
              <label htmlFor="ntsa-reg-input">Correct registration</label>
              <input
                id="ntsa-reg-input"
                type="text"
                value={ntsaPlateInput}
                onChange={(e) => setNtsaPlateInput(e.target.value.toUpperCase())}
                placeholder="e.g. KAN269U"
                autoComplete="off"
              />
            </div>
            {ntsaPlateStepResult && ntsaPlateStepResult.success === false && (
              <div className="paid-no-cert-modal-result error">
                <p>{ntsaPlateStepResult.error || ntsaPlateStepResult.message || 'Error'}</p>
              </div>
            )}
            <div className="paid-no-cert-modal-actions">
              <button
                type="button"
                className="btn-retry-date-submit"
                onClick={submitNtsaPlateCorrection}
                disabled={ntsaPlateSaving || !ntsaPlateInput.trim()}
              >
                {ntsaPlateSaving ? 'Applying…' : 'Apply and retry'}
              </button>
              <button type="button" className="btn-retry-date-cancel" onClick={closeNtsaPlateModal} disabled={ntsaPlateSaving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaidNoCertAttempt;
